import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, DollarSign, Megaphone, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";

/**
 * Public affiliate application page. Anyone can apply — students, influencers,
 * community organizers. Applications land as `status='pending'` in the
 * `affiliates` collection; admin approves them in /admin/affiliates. On
 * approval, the affiliate receives an email with their unique code +
 * private stats URL.
 *
 * Route: /affiliate (public)
 */
export default function AffiliateApplyPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country: "",
    promotion_channel: "",
    audience_size: "",
    payout_method: "",
    payout_email: "",
    heard_about_us: "",
    agreement_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.promotion_channel) {
      setErr("Please fill in name, email, and how you'll promote.");
      return;
    }
    if (!form.agreement_accepted) {
      setErr("Please accept the affiliate agreement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/affiliate/apply", form);
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AppShell>
        <div className="mx-auto max-w-xl px-6 py-20 text-center" data-testid="affiliate-thankyou">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
               style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}>
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">Application received</h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            Thanks, {form.name.split(" ")[0]}! We review applications within 2 business days. Once
            approved, you'll receive an email with your affiliate code, a share link, and a private
            stats page where you can track your earnings.
          </p>
          <Link to="/" className="btn-ghost text-sm" data-testid="affiliate-back-home">
            Back to home
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-6 py-10" data-testid="affiliate-apply-page">
        {/* Hero */}
        <div className="mb-8">
          <div className="pill pill-ai inline-flex mb-3">
            <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Affiliate program
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            Earn <span style={{ color: "var(--accent-copper)" }}>$200</span> for every Qualified Prime DS-260 Case you refer.
          </h1>
          <p className="mt-4 text-[16px] max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Students, community organizers, immigration content creators — share DS-260USA.com
            with people who need it, and we'll pay you a commission for every primary DS-260
            case that is paid AND accepted as complete by the client. Compensation is not tied to
            payment alone — it's earned when the client clicks "Accept Case as Complete and Ready
            for Filing".
          </p>
        </div>

        {/* How it works */}
        <div className="grid md:grid-cols-3 gap-4 mb-10" data-testid="affiliate-how-it-works">
          <HowStep n="1" title="Apply below"
                  body="Tell us who you are and how you'll promote. We'll approve you within 2 business days." />
          <HowStep n="2" title="Get your link"
                  body="You'll receive a unique share URL and a private stats page to track clicks & earnings." />
          <HowStep n="3" title="Get paid"
                  body="$200 per Qualified Prime DS-260 Case (client pays AND clicks 'Accept Case as Complete'). Paid monthly via your preferred method." />
        </div>

        {/* Form */}
        <form className="card-surface p-6 space-y-5" onSubmit={submit} data-testid="affiliate-form">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-5 h-5" style={{ color: "var(--accent-copper)" }} />
            <h2 className="font-display text-xl font-bold">Apply to become an affiliate</h2>
          </div>

          <FormRow label="Full name*" testId="affiliate-name">
            <input className="field-input" value={form.name} onChange={set("name")}
                   placeholder="Your full name" data-testid="affiliate-input-name" required />
          </FormRow>
          <FormRow label="Email*" testId="affiliate-email">
            <input type="email" className="field-input" value={form.email} onChange={set("email")}
                   placeholder="you@example.com" data-testid="affiliate-input-email" required />
          </FormRow>
          <FormRow label="Country you'll promote in" testId="affiliate-country">
            <input className="field-input" value={form.country} onChange={set("country")}
                   placeholder="USA, India, Philippines…" data-testid="affiliate-input-country" />
          </FormRow>
          <FormRow label="How will you promote DS-260USA.com?*"
                   hint="TikTok, YouTube, WeChat community, campus newsletter, immigration coach roster, church group, etc."
                   testId="affiliate-promo">
            <textarea className="field-input" rows={3} value={form.promotion_channel}
                      onChange={set("promotion_channel")}
                      placeholder="Describe your channel(s) and audience"
                      data-testid="affiliate-input-promo" required />
          </FormRow>
          <FormRow label="Approx audience size (optional)" testId="affiliate-audience">
            <input className="field-input" value={form.audience_size} onChange={set("audience_size")}
                   placeholder="e.g. 5k followers, 300-member community"
                   data-testid="affiliate-input-audience" />
          </FormRow>
          <div className="grid md:grid-cols-2 gap-4">
            <FormRow label="Preferred payout method" testId="affiliate-payout-method">
              <select className="field-input" value={form.payout_method}
                      onChange={set("payout_method")} data-testid="affiliate-input-payout-method">
                <option value="">Select…</option>
                <option value="Zelle">Zelle (US only)</option>
                <option value="PayPal">PayPal</option>
                <option value="Wise">Wise (international)</option>
                <option value="Bank transfer">Bank transfer</option>
                <option value="Other">Other</option>
              </select>
            </FormRow>
            <FormRow label="Payout email / handle" testId="affiliate-payout-email">
              <input className="field-input" value={form.payout_email} onChange={set("payout_email")}
                     placeholder="Where should we send your earnings?"
                     data-testid="affiliate-input-payout-email" />
            </FormRow>
          </div>
          <FormRow label="How did you hear about us? (optional)" testId="affiliate-heard">
            <input className="field-input" value={form.heard_about_us} onChange={set("heard_about_us")}
                   placeholder="Friend, Google, TikTok…" data-testid="affiliate-input-heard" />
          </FormRow>

          {err && (
            <div className="text-xs p-3 rounded-md" style={{ background: "#FEF2F2", color: "#991B1B" }}
                 data-testid="affiliate-error">
              {err}
            </div>
          )}

          {/* Mandatory agreement checkbox — Canonical Spec v1.2 §3.10 */}
          <div className="p-3 rounded-md" style={{ background: "var(--bg-cream)" }}>
            <label className="flex items-start gap-2 cursor-pointer text-xs" style={{ color: "var(--text-primary)" }}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.agreement_accepted}
                onChange={(e) => setForm({ ...form, agreement_accepted: e.target.checked })}
                data-testid="affiliate-input-agreement"
              />
              <span>
                I have read and accept the DS-260USA.com Affiliate Agreement:{" "}
                <em>
                  A referral may qualify for either the DS-260USA.com Friend &amp; Family
                  Referral Program or the DS-260USA.com Affiliate Referral Program, but not
                  both. Rewards and commissions are not stackable. Affiliate commissions are
                  payable only for Qualified Prime DS-260 Cases — defined as cases where the
                  client has clicked "Accept Case as Complete and Ready for Filing" and the case
                  status is <code>CASE_ACCEPTED</code>. Affiliates must generate at least two
                  Qualified Prime DS-260 Cases in any rolling 90-day period to maintain Active
                  Affiliate Status.
                </em>
              </span>
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={submitting} className="btn-primary"
                    data-testid="affiliate-submit">
              {submitting ? "Submitting…" : "Submit application"}
              <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              By applying, you agree to our <Link to="/legal/terms" className="underline">Terms of Service</Link>.
            </p>
          </div>
        </form>

        {/* FAQ */}
        <div className="mt-12 grid md:grid-cols-2 gap-4" data-testid="affiliate-faq">
          <FaqItem q="When am I paid?"
                   a="Compensation is earned only after (1) your referral pays for their case AND (2) they click 'Accept Case as Complete and Ready for Filing'. Payments then run monthly on the first business day of each month." />
          <FaqItem q="Do I earn for dependent family members?"
                   a="No. Commission is paid only on the Prime DS-260 case ($200 per Qualified Prime DS-260 Case). Additional family members on the same case do not earn additional commission." />
          <FaqItem q="Do I need to stay active?"
                   a="Yes — you must generate at least 2 Qualified Prime DS-260 Cases in any rolling 90-day period to maintain Active Affiliate Status. If you drop below, your UID keeps working but new conversions earn $0 until you're active again." />
          <FaqItem q="Is there a minimum payout?"
                   a="No minimum. If you have $200 earned, we pay $200." />
        </div>
      </div>
    </AppShell>
  );
}

function HowStep({ n, title, body }) {
  return (
    <div className="card-surface p-4" data-testid={`affiliate-how-step-${n}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center font-display font-extrabold mb-2"
           style={{ background: "var(--accent-copper)", color: "#FFFFFF" }}>
        {n}
      </div>
      <div className="font-display font-bold text-base mb-1">{title}</div>
      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{body}</div>
    </div>
  );
}

function FormRow({ label, hint, testId, children }) {
  return (
    <div data-testid={testId ? `${testId}-row` : undefined}>
      <label className="field-label">{label}</label>
      {children}
      {hint && (
        <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{hint}</p>
      )}
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="card-surface p-4">
      <summary className="cursor-pointer font-semibold text-sm">{q}</summary>
      <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>{a}</p>
    </details>
  );
}
